/**
 * Add or edit one of a customer's contacts (name, phone, email, role, primary,
 * texting OK). Moved out of the customer page so Closeout's "Add email" can
 * open the same editor (2026-10-17).
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

/** The contact fields the editor reads (a customer_contacts row). */
export interface ContactFormContact {
    id: string;
    first_name: string;
    last_name: string | null;
    email: string | null;
    phone: string | null;
    role_title: string | null;
    is_primary: boolean;
    sms_consent?: string | null;
}

export function ContactFormDialog({
    customerId,
    contact,
    onClose,
    onSaved,
    title,
    emailFirst,
}: {
    customerId: string;
    contact?: ContactFormContact;
    onClose: () => void;
    /** Runs after a successful save, before the dialog closes. */
    onSaved?: () => void;
    /** Overrides the "Add contact" / "Edit contact" heading. */
    title?: string;
    /** Put the cursor in Email instead of First name. */
    emailFirst?: boolean;
}) {
    const qc = useQueryClient();
    const isEdit = !!contact;
    const [firstName, setFirstName] = useState(contact?.first_name ?? "");
    const [lastName, setLastName] = useState(contact?.last_name ?? "");
    const [phone, setPhone] = useState(contact?.phone ?? "");
    const [email, setEmail] = useState(contact?.email ?? "");
    const [roleTitle, setRoleTitle] = useState(contact?.role_title ?? "");
    const [isPrimary, setIsPrimary] = useState(contact?.is_primary ?? false);
    const [smsConsent, setSmsConsent] = useState(contact?.sms_consent === "opted_in");

    const save = useMutation({
        mutationFn: async () => {
            const body = {
                firstName: firstName.trim(),
                lastName: lastName.trim() || null,
                phone: phone.trim() || null,
                email: email.trim() || null,
                roleTitle: roleTitle.trim() || null,
                isPrimary,
            };
            const saved = isEdit
                ? await api.patch<{ contact: { id: string } }>(
                      `/api/customers/${customerId}/contacts/${contact!.id}`,
                      body,
                  )
                : await api.post<{ contact: { id: string } }>(`/api/customers/${customerId}/contacts`, body);
            // The texting-consent toggle routes through its own audited endpoint
            // (records timestamp + source). Only call it when the office actually
            // changed the box, so we don't re-stamp consent on every contact edit.
            const savedContactId = saved?.contact?.id ?? (isEdit ? contact!.id : undefined);
            const wasConsented = contact?.sms_consent === "opted_in";
            if (savedContactId && smsConsent !== wasConsented) {
                await api.patch(`/api/customers/${customerId}/contacts/${savedContactId}/sms-consent`, {
                    state: smsConsent ? "opted_in" : "opted_out",
                });
            }
            return saved;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["customer", customerId, "contacts"] });
            qc.invalidateQueries({ queryKey: ["customer-contacts", customerId] });
            toast.success(isEdit ? "Contact updated" : "Contact added");
            onSaved?.();
            onClose();
        },
        onError: () => toast.error(isEdit ? "Couldn't update contact" : "Couldn't add contact"),
    });

    return (
        <Dialog open onClose={onClose} title={title ?? (isEdit ? "Edit contact" : "Add contact")}>
            <form
                className="space-y-3"
                onSubmit={(e) => {
                    e.preventDefault();
                    if (!firstName.trim()) {
                        toast.error("First name is required");
                        return;
                    }
                    if (emailFirst && !email.trim()) {
                        toast.error("Type the email address first");
                        return;
                    }
                    save.mutate();
                }}
            >
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs font-medium">First name *</label>
                        <Input
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            autoFocus={!emailFirst}
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium">Last name</label>
                        <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs font-medium">Phone</label>
                        <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                    </div>
                    <div>
                        <label className="text-xs font-medium">Email</label>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoFocus={emailFirst}
                        />
                    </div>
                </div>
                <div>
                    <label className="text-xs font-medium">Role / title</label>
                    <Input
                        value={roleTitle}
                        onChange={(e) => setRoleTitle(e.target.value)}
                        placeholder="e.g. Facilities manager"
                    />
                </div>
                <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={isPrimary} onCheckedChange={(v) => setIsPrimary(v === true)} />
                    Primary contact for this customer
                </label>
                <label className="flex items-start gap-2 text-sm">
                    <Checkbox
                        className="mt-0.5"
                        checked={smsConsent}
                        onCheckedChange={(v) => setSmsConsent(v === true)}
                    />
                    <span>
                        Texting OK <span className="text-foreground/60">(optional — never required)</span>
                        <span className="block text-xs text-foreground/60">
                            Send this contact appointment and service texts. Turn off any time; the customer can reply
                            STOP.
                        </span>
                    </span>
                </label>
                <div className="flex justify-end gap-2 pt-1">
                    <Button type="button" variant="outline" size="sm" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" size="sm" disabled={save.isPending}>
                        {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Add contact"}
                    </Button>
                </div>
            </form>
        </Dialog>
    );
}
